import { useLocation } from '@reach/router';
import React, { useContext, useEffect, useState } from 'react';

import { PreviewContext } from '../../context/PreviewContext';
import { getCookie } from '../../utils/cookie-helpers';
import { CardLayout } from './card-layout';
import { DefaultLayout } from './default-layout';
import { PostLayout } from './post-layout';

const statesMap = {
	AL: 'alabama',
	AK: 'alaska',
	AZ: 'arizona',
	AR: 'arkansas',
	CA: 'california',
	CO: 'colorado',
	CT: 'connecticut',
	DE: 'delaware',
	FL: 'florida',
	GA: 'georgia',
	HI: 'hawaii',
	ID: 'idaho',
	IL: 'illinois',
	IN: 'indiana',
	IA: 'iowa',
	KS: 'kansas',
	KY: 'kentucky',
	LA: 'louisiana',
	ME: 'maine',
	MD: 'maryland',
	MA: 'massachusetts',
	MI: 'michigan',
	MN: 'minnesota',
	MS: 'mississippi',
	MO: 'missouri',
	MT: 'montana',
	NE: 'nebraska',
	NV: 'nevada',
	NH: 'new-hampshire',
	NJ: 'new-jersey',
	NM: 'new-mexico',
	NY: 'new-york',
	NC: 'north-carolina',
	ND: 'north-dakota',
	OH: 'ohio',
	OK: 'oklahoma',
	OR: 'oregon',
	PA: 'pennsylvania',
	RI: 'rhode-island',
	SC: 'south-carolina',
	SD: 'south-dakota',
	TN: 'tennessee',
	TX: 'texas',
	UT: 'utah',
	VT: 'vermont',
	VA: 'virginia',
	WA: 'washington',
	WV: 'west-virginia',
	WI: 'wisconsin',
	WY: 'wyoming',
	DC: 'district-of-columbia',
};

const modalityCategoriesMap = {
	category_cognitive_and_behavioral_therapies: [
		'modality_acceptance_and_committment_therapy',
		'modality_applied_behavioral_analysis_aba',
		'modality_behavioral_therapies',
		'modality_cognitive_behavioral_therapy',
		'modality_cognitive_behavioral_therapy',
		'modality_dialectical_behavioral_therapy',
		'modality_exposure_and_response_prevention',
		'modality_motivational_interviewing',
		'modality_rational_emotive_behavior_therapy',
		'modality_solution_focused_therapy',
	],
	category_trauma_focused_therapies: [
		'modality_cognitive_processing_therapy',
		'modality_eye_movement_desensitization',
		'modality_prolonged_exposure_pe',
		'modality_trauma_focused_cognitive_behavior_therapy',
	],
	category_mindfulness_based_therapies: [
		'modality_mbct_compassion_focused_therapy',
		'modality_mindfulness_based_stress_reduction_mbsr',
	],
	category_body_focused_therapies: [
		'modality_biofeedback',
		'modality_hypnotherapy',
		'modality_neuro_linguistic_programming_therapy',
		'modality_somatic_experiencing_sensorimotor_therapy',
	],
	category_insight_oriented_therapies: [
		'modality_attachment_focused_psychotherapy',
		'modality_emotional_focused_therapy',
		'modality_internal_family_systems',
		'modality_interpersonal_psychotherapy',
		'modality_multicultural_culturally_informed_therapy',
		'modality_narrative_therapy',
		'modality_psychoanalysis',
		'modality_psychodynamic_therapy',
	],
	category_couples_marital_relationship_therapy: [
		'modality_emotion_focused_therapy_for_couples',
		'modality_gottman_method',
	],
};

const modalityCategoriesContentMap = {
	category_body_focused_therapies: 'body-focused',
	category_cognitive_and_behavioral_therapies: 'cognitive behavioral',
	category_couples_marital_relationship_therapy: 'couples',
	category_insight_oriented_therapies: 'insight-oriented',
	category_mindfulness_based_therapies: 'mindfulness-based',
	category_trauma_focused_therapies: 'trauma-focused',
};

interface Provider {
	id: string;
	firstName: string;
	lastName: string;
	profileImage: string;
	specialties: string;
	availability?: string;
	acceptedInsuranceSlugs: string[];
	offersVirtual: boolean;
	slug: string;
	filterables?: { slug: string; name: string }[];
	selfScheduleAvailability?: string;
	profilePhoto: string;
	providerId: string;
	providerFirstName: string;
	providerLastName: string;
	providerSlug: string;
}

interface ProviderProfilesProps {
	abTestId: string;
	ctaAnalytics: Record<string, unknown>;
	ctaCopy?: string;
	filterables: {
		_type: string;
		state?: string;
		numberResults?: number;
		modality?: string;
		modalityCategory?: string;
	}[];
	layoutType: 'default' | 'card' | 'post';
}

export function ProviderProfiles({
	abTestId,
	ctaAnalytics,
	ctaCopy,
	filterables,
	layoutType = 'default',
}: ProviderProfilesProps) {
	// hooks
	const location = useLocation();
	const { moduleAbTestValue } = useContext(PreviewContext);

	const formattedModuleAbTestValue = moduleAbTestValue?.toLowerCase();

	// get state name from path
	const pathname = location.pathname;
	const pathParts = pathname.split('/');
	const stateNameInPath =
		pathParts.length > 1 ? pathParts[pathParts.length - 2] : '';

	// state
	const [apiUrl, setApiUrl] = useState('');
	const [count, setCount] = useState(0);
	const [error, setError] = useState<Error | null>(null);
	const [modalityCategoryCopy, setModalityCategoryCopy] = useState('');
	const [combinedFilterables, setCombinedFilterables] = useState('');
	const [loading, setLoading] = useState(true);
	const [providers, setProviders] = useState<Provider[]>([]);
	const [providerDisplayCount, setProviderDisplayCount] = useState(0);

	// exclude providers with first names that match common drug names
	// paid campaigns through Google will show up with lower results if we don't do this
	const namesToFilter = [
		'Avita',
		'Briellyn',
		'Jalyn',
		'Loryna',
		'Yasmin',
		'Yaz',
		'Yazmin',
		'Ziana',
	];

	// manually set filterables
	const manualFilterables = [
		'availability',
		'teletherapy_preference_online',
		'session_availability_monday_afternoon',
		'session_availability_monday_early_morning',
		'session_availability_monday_late_morning',
		'session_availability_monday_evening',
		'session_availability_tuesday_afternoon',
		'session_availability_tuesday_early_morning',
		'session_availability_tuesday_late_morning',
		'session_availability_tuesday_evening',
		'session_availability_wednesday_afternoon',
		'session_availability_wednesday_early_morning',
		'session_availability_wednesday_late_morning',
		'session_availability_wednesday_evening',
		'session_availability_thursday_afternoon',
		'session_availability_thursday_early_morning',
		'session_availability_thursday_late_morning',
		'session_availability_thursday_evening',
		'session_availability_friday_afternoon',
		'session_availability_friday_early_morning',
		'session_availability_friday_late_morning',
		'session_availability_friday_evening',
		'session_availability_saturday_afternoon',
		'session_availability_saturday_early_morning',
		'session_availability_saturday_late_morning',
		'session_availability_saturday_evening',
		'session_availability_sunday_afternoon',
		'session_availability_sunday_early_morning',
		'session_availability_sunday_late_morning',
		'session_availability_sunday_evening',
	];

	// Process filterables and construct URL
	useEffect(() => {
		if (filterables && Array.isArray(filterables)) {
			const statesArray: string[] = [];
			const modalitiesArray: string[] = [];
			const multiplier = 4;

			let resultCount = 10;

			filterables.forEach((filter) => {
				if (
					filter._type === 'providerSearchStateFilter' &&
					filter.state
				) {
					if (filter.state === 'dynamic') {
						// Extract state from pathname if it exists
						const pathParts = pathname.split('/');

						const stateFromUrl = pathParts.find((part) => {
							const matchedState = Object.entries(statesMap).find(
								([, value]) => value === part
							);
							return matchedState?.[0] !== undefined;
						});

						const matchedStateAbbr = stateFromUrl
							? Object.entries(statesMap).find(
									([, value]) => value === stateFromUrl
								)?.[0]
							: null;

						if (matchedStateAbbr) {
							statesArray.push(matchedStateAbbr);
						} else {
							console.log('No state found in URL path');
						}
					} else {
						statesArray.push(filter.state);
					}
				} else if (
					filter._type === 'providerSearchModalityFilter' &&
					filter.modality
				) {
					modalitiesArray.push(filter.modality);
				} else if (
					filter._type === 'providerSearchModalityCategoryFilter' &&
					filter.modalityCategory
				) {
					const modalityCategories =
						modalityCategoriesMap[filter.modalityCategory];

					modalitiesArray.push(...modalityCategories);

					const modalityCategoryCopy =
						modalityCategoriesContentMap[filter.modalityCategory];

					setModalityCategoryCopy(modalityCategoryCopy);
				} else if (
					filter._type === 'providerSearchResultCountFilter' &&
					filter.numberResults
				) {
					setProviderDisplayCount(filter.numberResults);

					// we are overfetching by 4x in this case to be able to manually sort out names and randomize order on load
					resultCount = filter.numberResults * multiplier;
				}
			});

			const baseUrl =
				'https://secure.helloalma.com/api/v1/providerProfiles/search/?page=1';

			// state filterables from cms
			const stateParam = statesArray
				.map((state) => `&state=${state}`)
				.join('');

			// modality filterables from cms
			const modalityParam = modalitiesArray
				.map((modality) => modality)
				.join(',');

			// manually set filterables
			const manualFilterablesParam = manualFilterables
				.map((filter) => filter)
				.join(',');

			// combine modality and manual filterables
			const combinedFilterables = `${manualFilterablesParam},${modalityParam}`;

			// final params
			const filterablesParam = `&filterables=${combinedFilterables}`;
			const resultParam = `&limit=${resultCount}`;

			const url = `${baseUrl}${resultParam}${stateParam}${filterablesParam}`;

			setApiUrl(url);
			setCombinedFilterables(combinedFilterables);
		}
	}, [filterables]);

	// Fetch data when URL changes
	useEffect(() => {
		const fetchProviders = async () => {
			if (!apiUrl) return;

			setLoading(true);

			try {
				const response = await fetch(apiUrl);
				const data = await response.json();

				const providerCount = data.count;
				const providerResults = data.results as Provider[];

				// filter out providers with first names that match common drug names
				// filter out providers with no profile photo
				// filter out providers that have no modalities in their filterables
				const filteredResults = providerResults.filter(
					(provider: Provider) =>
						!namesToFilter.includes(provider.providerFirstName) &&
						provider.profilePhoto !== null &&
						provider.filterables?.some(
							(filterable) =>
								filterable.slug &&
								filterable.slug.includes('modality')
						)
				);

				// randomize the results
				const randomizeResults = filteredResults.sort(
					() => Math.random() - 0.5
				);

				// display the results based on count set in filterables in cms
				const displayResults = randomizeResults.slice(
					0,
					providerDisplayCount
				);

				setCount(providerCount);
				setProviders(displayResults);
			} catch (err) {
				setError(err as Error);
			} finally {
				setLoading(false);
			}
		};

		void fetchProviders();
	}, [apiUrl]);

	if (loading) {
		return <div>Loading...</div>;
	}

	if (error) {
		console.log('Error loading providers: ', error);
		return null;
	}

	const stateNameLowercase = stateNameInPath
		? Object.entries(statesMap).find(
				([, value]) => value === stateNameInPath
			)?.[1]
		: '';

	const stateName = stateNameLowercase
		? stateNameLowercase.charAt(0).toUpperCase() +
			stateNameLowercase.slice(1)
		: '';

	const stateAbbreviation = stateName
		? Object.entries(statesMap)
				.find(([, value]) => value === stateName.toLowerCase())?.[0]
				.toLowerCase()
		: '';

	const elid = getCookie('elid') ?? 'anonymous';

	const baseUrl = 'https://secure.helloalma.com/providers-results/';

	const encodedFilterables = btoa(combinedFilterables);
	const filterablesParam = `?filterables=${encodedFilterables}`;

	const ctaLink = `${baseUrl}${stateAbbreviation ? stateAbbreviation : ''}${
		encodedFilterables ? filterablesParam : ''
	}${
		formattedModuleAbTestValue
			? `${encodedFilterables ? '&' : '?'}ld_user_key=${elid}&ab=${formattedModuleAbTestValue}`
			: ''
	}`;

	// layouts
	const defaultLayout = layoutType === 'default';
	const cardLayout = layoutType === 'card';
	const postLayout = layoutType === 'post';

	if (defaultLayout) {
		return (
			<DefaultLayout
				abTestId={abTestId}
				count={count}
				ctaAnalytics={ctaAnalytics}
				ctaLink={ctaLink}
				elid={elid}
				formattedModuleAbTestValue={formattedModuleAbTestValue}
				providers={providers}
				stateName={stateName}
			/>
		);
	}

	if (cardLayout) {
		return (
			<CardLayout
				count={count}
				ctaLink={ctaLink}
				modalityCategoryCopy={modalityCategoryCopy}
				providers={providers}
			/>
		);
	}

	if (postLayout) {
		return (
			<PostLayout
				abTestId={abTestId}
				ctaAnalytics={ctaAnalytics}
				ctaCopy={ctaCopy}
				ctaLink={ctaLink}
				elid={elid}
				formattedModuleAbTestValue={formattedModuleAbTestValue}
				providers={providers}
			/>
		);
	}
}
